import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Alert, ProgressBar, Form, InputGroup } from 'react-bootstrap';
import api from './api';
import './CommunityNotes.css';

const CommunityNotes = () => {
  const [notes, setNotes] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [courses, setCourses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLoading, setFilterLoading] = useState(false);
  
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;
  const navigate = useNavigate();

  useEffect(() => {
    // Load saved filters from localStorage
    const savedCourse = localStorage.getItem('communityNotes_selectedCourse');
    const savedTopic = localStorage.getItem('communityNotes_selectedTopic');
    
    if (savedCourse) setSelectedCourse(savedCourse);
    if (savedTopic) setSelectedTopic(savedTopic);
    
    fetchCourses();
    fetchCommunityNotes();
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetchTopics(selectedCourse);
    } else {
      setTopics([]);
      setSelectedTopic('');
    }
  }, [selectedCourse]);

  useEffect(() => {
    // Save filters to localStorage
    if (selectedCourse) {
      localStorage.setItem('communityNotes_selectedCourse', selectedCourse);
    } else {
      localStorage.removeItem('communityNotes_selectedCourse');
    }
    
    fetchCommunityNotes();
  }, [selectedCourse, selectedTopic]);

  useEffect(() => {
    // Save topic filter to localStorage
    if (selectedTopic) {
      localStorage.setItem('communityNotes_selectedTopic', selectedTopic);
    } else {
      localStorage.removeItem('communityNotes_selectedTopic');
    }
  }, [selectedTopic]);

  const fetchCourses = async () => {
    try {
      const response = await api.get('api/courses/');
      setCourses(response.data || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchTopics = async (courseId) => {
    try {
      setFilterLoading(true);
      const response = await api.get(`api/list_topics/${courseId}`);
      setTopics(response.data || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setTopics([]);
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchCommunityNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (selectedCourse) params.append('course_id', selectedCourse);
      if (selectedTopic) params.append('topic_id', selectedTopic);
      
      const queryString = params.toString();
      const url = `api/community-notes/${queryString ? `?${queryString}` : ''}`;
      
      console.log('Fetching community notes from:', url);
      
      const config = {};
      if (userInfo?.token) {
        config.headers = {
          'Authorization': `Bearer ${userInfo.token}`
        };
      }
      
      const response = await api.get(url, config);
      console.log('Community notes response:', response.data);
      
      // Handle different response formats
      if (response.data && typeof response.data === 'object') {
        if (response.data.notes) {
          // New format with notes and user_progress
          setNotes(response.data.notes || []);
          setUserProgress(response.data.user_progress || null);
        } else if (Array.isArray(response.data)) {
          // Simple array format
          setNotes(response.data);
          setUserProgress(null);
        } else {
          // Fallback for other formats
          setNotes([]);
          setUserProgress(null);
        }
      } else {
        setNotes([]);
        setUserProgress(null);
      }
    } catch (err) {
      console.error('Error fetching community notes:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to load community notes';
      if (err.response?.status === 404) {
        errorMessage = 'Community notes endpoint not found. The server may need to be updated with the new API endpoints.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. The database may need migrations or the server needs to be restarted.';
      } else if (err.response?.status === 405) {
        errorMessage = 'Method not allowed. The API endpoint may not be properly configured.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please check your connection.';
      }
      
      // Set mock data for development/testing
      if (process.env.NODE_ENV === 'development') {
        console.log('Using mock data for development');
        setNotes([
          {
            id: 1,
            title: "Sample Study Notes",
            preview_text: "This is a sample community note to demonstrate the interface...",
            course: "Sample Course",
            topic: "Sample Topic",
            original_author: "Demo User",
            views: 42,
            likes_count: 5,
            user_liked: false,
            required_level: 1,
            is_accessible: true,
            file_type: "pdf",
            file_size: 1024000,
            file_name: "sample_notes.pdf",
            s3_url: "https://example-bucket.s3.amazonaws.com/sample_notes.pdf",
            link: "https://example.com/external-resource",
            page_count: 10,
            word_count: 2500,
            created_at: new Date().toISOString()
          }
        ]);
        setUserProgress({
          current_level: 1,
          current_xp: 100,
          accessible_notes: 1,
          locked_notes: 0,
          total_notes: 1
        });
        setError(null);
        return;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const handleLike = async (noteId) => {
    try {
      const config = {
        headers: {
          'Authorization': `Bearer ${userInfo?.token}`
        }
      };
      
      await api.post(`api/community-notes/${noteId}/like/`, {}, config);
      fetchCommunityNotes(); // Refresh to update like count
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  // Filter notes by search term
  const filteredNotes = notes.filter(note => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      note.title.toLowerCase().includes(searchLower) ||
      note.preview_text.toLowerCase().includes(searchLower) ||
      note.course.toLowerCase().includes(searchLower) ||
      note.topic.toLowerCase().includes(searchLower) ||
      (note.is_accessible && note.original_author.toLowerCase().includes(searchLower))
    );
  });

  if (loading) return (
    <Container className="mt-4">
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading community notes...</p>
      </div>
    </Container>
  );
  
  if (error) return (
    <Container className="mt-4">
      <Alert variant="danger">
        <Alert.Heading>Unable to Load Community Notes</Alert.Heading>
        <p>{error}</p>
        <hr />
        <div className="d-flex justify-content-end">
          <Button onClick={() => window.location.reload()} variant="outline-danger">
            Try Again
          </Button>
        </div>
      </Alert>
    </Container>
  );

  return (
    <div className="community-notes-container" style={{ minHeight: '100vh', overflowY: 'auto' }}>
      <Container fluid className="mt-4 px-3 pb-5">
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="mb-0">Community Notes</h2>
              {userInfo && (
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/community-notes/upload')}
                >
                  <i className="bi bi-upload"></i> Upload Document
                </Button>
              )}
            </div>
          
          {/* User Progress Section */}
          {userProgress && userInfo && (
            <Card className="mb-4 progress-card">
              <Card.Body className="py-3">
                <Row>
                  <Col xs={12} md={6} className="mb-3 mb-md-0">
                    <h6>Your Progress</h6>
                    <div className="small">
                      <strong>Level:</strong> {userProgress.current_level} | <strong>XP:</strong> {userProgress.current_xp}
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <h6>Notes Access</h6>
                    <div className="small mb-2">
                      <strong>Accessible:</strong> {userProgress.accessible_notes} / {userProgress.total_notes} | <strong>Locked:</strong> {userProgress.locked_notes}
                    </div>
                    <ProgressBar 
                      now={(userProgress.accessible_notes / userProgress.total_notes) * 100} 
                      label={`${Math.round((userProgress.accessible_notes / userProgress.total_notes) * 100)}%`}
                      style={{ height: '20px' }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Filters Section */}
          <Card className="mb-4 filter-card">
            <Card.Body className="py-3">
              <Row className="align-items-end">
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label className="small fw-bold">Course</Form.Label>
                    <Form.Select 
                      value={selectedCourse} 
                      onChange={(e) => setSelectedCourse(e.target.value)}
                      size="sm"
                    >
                      <option value="">All Courses</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                  <Form.Group>
                    <Form.Label className="small fw-bold">Topic</Form.Label>
                    <Form.Select 
                      value={selectedTopic} 
                      onChange={(e) => setSelectedTopic(e.target.value)}
                      disabled={!selectedCourse || filterLoading}
                      size="sm"
                    >
                      <option value="">All Topics</option>
                      {topics.map(topic => (
                        <option key={topic.id} value={topic.id}>
                          {topic.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="small fw-bold">Search</Form.Label>
                    <InputGroup size="sm">
                      <Form.Control
                        type="text"
                        placeholder="Search notes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => setSearchTerm('')}
                        >
                          ✕
                        </Button>
                      )}
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
              
              {(selectedCourse || selectedTopic || searchTerm) && (
                <Row className="mt-3">
                  <Col>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <span className="small text-muted">Active filters:</span>
                      {selectedCourse && (
                        <Badge bg="primary" className="d-flex align-items-center gap-1">
                          Course: {courses.find(c => c.id == selectedCourse)?.name}
                          <button 
                            className="btn-close btn-close-white" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => setSelectedCourse('')}
                          ></button>
                        </Badge>
                      )}
                      {selectedTopic && (
                        <Badge bg="info" className="d-flex align-items-center gap-1">
                          Topic: {topics.find(t => t.id == selectedTopic)?.title}
                          <button 
                            className="btn-close btn-close-white" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => setSelectedTopic('')}
                          ></button>
                        </Badge>
                      )}
                      {searchTerm && (
                        <Badge bg="success" className="d-flex align-items-center gap-1">
                          Search: "{searchTerm}"
                          <button 
                            className="btn-close btn-close-white" 
                            style={{ fontSize: '0.6em' }}
                            onClick={() => setSearchTerm('')}
                          ></button>
                        </Badge>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {/* Debug Info - Remove this after testing */}
          {process.env.NODE_ENV === 'development' && (
            <Alert variant="info" className="mb-3">
              <strong>Debug Info:</strong><br/>
              Total notes: {notes.length}<br/>
              Filtered notes: {filteredNotes.length}<br/>
              User logged in: {userInfo ? 'Yes' : 'No'}<br/>
              User level: {userProgress?.current_level || 'Unknown'}
            </Alert>
          )}

          {/* Notes List */}
          <Row className="g-3">
            {filteredNotes.map(note => (
              <Col xs={12} sm={6} lg={4} xl={3} key={note.id}>
                <Card className={`note-card h-100 ${!note.is_accessible ? 'locked border-warning' : ''}`} style={{ minHeight: '280px', maxHeight: '400px', overflow: 'hidden' }}>
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Card.Title className="h6 text-truncate me-2" title={note.title}>
                        {note.title}
                      </Card.Title>
                      <Badge 
                        bg={note.is_accessible ? 'success' : 'warning'} 
                        className={`level-badge flex-shrink-0 ${note.is_accessible ? 'accessible' : 'locked'}`}
                      >
                        L{note.required_level}
                      </Badge>
                    </div>
                    
                    <div className="small text-muted mb-2">
                      {note.course} → {note.topic}
                    </div>
                    
                    <div className="flex-grow-1 mb-3">
                      <div className="small note-preview">
                        {note.preview_text}
                      </div>
                    </div>
                    
                    {note.is_accessible && (
                      <div className="small text-muted mb-2">
                        <div>📄 {note.file_type?.toUpperCase()} • {(note.file_size / 1024 / 1024).toFixed(1)} MB</div>
                        <div>By {note.original_author} • {note.views} views • {note.likes_count} likes</div>
                        {note.page_count && <div>📑 {note.page_count} pages • {note.word_count} words</div>}
                      </div>
                    )}
                    
                    <div className="mt-auto">
                      {/* Debug info per note - Remove after testing */}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="small text-info mb-2">
                          Debug: is_accessible={note.is_accessible ? 'true' : 'false'}, required_level={note.required_level}
                        </div>
                      )}
                      
                      {note.is_accessible ? (
                        <div className="d-flex gap-2 flex-wrap">
                          {note.s3_url && (
                            <a 
                              href={note.s3_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-sm"
                            >
                              📄 View File
                            </a>
                          )}
                          {note.link && (
                            <a 
                              href={note.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-success btn-sm"
                            >
                              🔗 External Link
                            </a>
                          )}
                          {!note.s3_url && !note.link && (
                            <Button 
                              variant="outline-secondary" 
                              size="sm" 
                              disabled
                            >
                              📄 No File Available
                            </Button>
                          )}
                          {userInfo && (
                            <Button 
                              variant={note.user_liked ? "danger" : "outline-danger"} 
                              size="sm"
                              onClick={() => handleLike(note.id)}
                            >
                              ❤️ {note.likes_count}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Alert variant="warning" className="small p-2 m-0">
                          🔒 Reach Level {note.required_level} to unlock
                        </Alert>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          
          {filteredNotes.length === 0 && notes.length > 0 && (
            <Alert variant="info" className="mt-4">
              No notes match your current filters. Try adjusting your search or filters.
            </Alert>
          )}
          
          {notes.length === 0 && (
            <Alert variant="info" className="mt-4">
              No community notes available yet. Be the first to contribute!
            </Alert>
          )}
        </Col>
      </Row>
    </Container>
    </div>
  );
};

export default CommunityNotes;